import React, { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import DefaultTagRenderer from './DefaultTagRenderer';
import createFakeEvent from '../../lib/createFakeEvent';

/**
 * `Tags` is a <a href="https://reactjs.org/docs/fragments.html">fragment</a> component that renders simple tag labels with
 * an optional delete icon. It accepts either a string separated by a *delimiter*, or an `array` of `strings`.
 *
 * The delete icon will only be shown when *onDelete* is defined.  *onDelete* will be invoked when the user clicks on the tag
 * and deletes. The callback's `event.target.value` will be the same type you pass as *value*.
 *
 * This component will render nothing if *value* is empty or `undefined`.
 *
 * Custom rendering of tags can be applied using the *itemRenderer* prop.
 * <a href="https://github.com/vimond/vimond-core-components/blob/master/src/components/Tags/DefaultTagsRenderer.jsx">
 *   <code>DefaultTagsRenderer</code>
 * </a>
 * will be used as default renderer if no *itemRenderer* is set.
 *
 * @property {string} className A class name that will be put on each tag. Will be appended to "core-tag".
 * @property {string} delimiter The delimiter to use splitting a *value* string into tags. This prop has no meaning if you
 * pass an array of strings as *value*. Defaults to ",".
 * @property {bool} disabled If set to `true`, all tags will be disabled and cannot be deleted. Defaults to `false`.
 * @property {function} itemRenderer The tag renderer callback that will be called for all tags in *value*.
 * <a href="https://github.com/vimond/vimond-core-components/blob/master/src/components/Tags/DefaultTagsRenderer.jsx">
 *   <code>DefaultTagsRenderer</code>
 * </a>
 * will be used as default renderer if no *itemRenderer* is set.
 * @property {function} onDelete A callback that is called whenever the user has deleted a tag. `event.target.value` will be
 * the new tag value and it will be on the same format as your *value* prop (that is, if you pass a comma separated string
 * you will get a comma separated string back **without** the deleted tag). A second parameter will be passed to *onDelete*
 * indicating the position that the tag was deleted from.
 * @property {string|array} value Either a string separated by a *delimiter*, or an array of strings (one for each tag),
 * alternatively an array of objects if you are using a custom *itemRenderer. Defaults to [].
 *
 * @see TagInput
 */
export default class Tags extends Component {

  static displayName = 'Tags';

  static propTypes = {
    className: PropTypes.string,
    delimiter: PropTypes.string,
    disabled: PropTypes.bool,
    itemRenderer: PropTypes.func,
    onDelete: PropTypes.func,
    style: PropTypes.object,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.array,
    ]),
  };

  static defaultProps = {
    delimiter: ',',
    value: [],
  };

  onDelete = e => {
    const { delimiter, disabled, onDelete } = this.props;

    if (!disabled && onDelete) {
      const tag = e.currentTarget.dataset.tag;
      const value = this.getTagArray().slice();
      const index = value.indexOf(tag);

      value.splice(index, 1);
      if (typeof this.props.value === 'string') {
        onDelete(createFakeEvent(value.join(delimiter), e), index);
      } else {
        onDelete(createFakeEvent(value, e), index);
      }
    }
  };

  getTagArray = () => typeof this.props.value === 'string' ? this.props.value.split(this.props.delimiter) : this.props.value;

  itemRenderer = (tag, disabled, onClick) => <DefaultTagRenderer key={tag} tag={tag} disabled={disabled} onClick={onClick} style={this.props.style}/>;

  render() {
    const { disabled, itemRenderer = this.itemRenderer, onDelete, value } = this.props;

    if (value && value.length) {
      return (
        <Fragment>
          {this.getTagArray().map(tag => itemRenderer(tag, disabled, onDelete && this.onDelete))}
        </Fragment>
      );
    }
    return null;
  }

}
